// Taxonomie des passions (événements) et des powers (services) — organisée par famille pour
// qu'un utilisateur trouve quasiment toujours ce qu'il cherche, et pour que ces familles
// servent aussi de base aux badges d'identité (une famille vécue plusieurs fois = un badge).
export type CategoryGroup = { family: string; items: string[] };

export const PASSION_GROUPS: CategoryGroup[] = [
  {
    family: "Sorties",
    items: ["Cinéma", "Restaurant", "Dîner entre amis", "Concert", "Festival", "Théâtre", "Opéra", "Afterwork", "Bar", "Soirée", "Soirée d'anniversaire", "Voyage", "Week-end", "Visite", "Excursion", "Spectacle", "Musée", "Exposition", "Plage", "Sortie"],
  },
  {
    family: "Sport",
    items: ["Football", "Tennis", "Padel", "Basketball", "Volleyball", "Rugby", "Golf", "Randonnée", "Trail", "Running", "Jogging", "Cyclisme", "Vélo", "Escalade", "Ski", "Snowboard", "Surf", "Natation", "Yoga", "Pilates", "Danse", "Arts martiaux", "Boxe", "Fitness", "Musculation", "Badminton", "Squash", "Ping-pong", "Handball", "Hockey", "Beach volley", "Roller", "Skateboard", "Plongée", "Kitesurf", "Nautisme", "Karting", "Canoë-kayak", "Tir à l'arc", "Pétanque", "Zumba", "Méditation"],
  },
  {
    family: "Culture",
    items: ["Musées", "Expositions", "Théâtre", "Opéra", "Concerts", "Littérature", "Lecture", "Club de lecture", "Écriture", "Histoire", "Architecture", "Patrimoine", "Photographie", "Photo urbaine", "Peinture", "Dessin & illustration", "Sculpture", "Cinéma", "Art contemporain", "Philosophie", "Conférences", "Débats", "Visites guidées", "Chant choral", "Fresque murale"],
  },
  {
    family: "Sciences & intellect",
    items: ["Astronomie", "Observation des étoiles", "Sciences", "Technologie", "Robotique", "Informatique", "Intelligence artificielle", "Biologie", "Géologie", "Archéologie", "Physique", "Mathématiques", "Conférences scientifiques", "Vulgarisation scientifique", "Ateliers scientifiques", "Découverte de laboratoires", "Échecs", "Bridge"],
  },
  {
    family: "Gastronomie",
    items: ["Dégustation de vins", "Œnologie", "Dégustation de bière", "Gastronomie", "Cours de cuisine", "Pâtisserie", "Fromages", "Chocolat", "Marchés", "Produits locaux", "Découverte de producteurs", "Visites de domaines", "Repas entre particuliers", "Expériences culinaires", "Circuit gastronomique", "Cocktails & mixologie", "Pique-nique"],
  },
  {
    family: "Shopping & découvertes",
    items: ["Shopping", "Boutiques", "Marchés aux puces", "Brocantes", "Antiquités", "Vide-greniers", "Salons", "Foires", "Concept stores", "Découverte de commerces", "Créateurs locaux"],
  },
  {
    family: "Voyages",
    items: ["Voyages", "Road trips", "Week-ends", "Excursions", "Découverte de villes", "Découverte de villages", "Tourisme local", "Activités touristiques"],
  },
  {
    family: "Loisirs & jeux",
    items: ["Jeux de société", "Karaoké", "Escape game", "Laser game", "Poker", "Soirée jeux vidéo", "Jardinage collectif", "Tricot & crochet", "Atelier céramique"],
  },
  { family: "Autre", items: ["Libre"] },
];

export const POWER_GROUPS: CategoryGroup[] = [
  {
    family: "Maison & bricolage",
    items: ["Bricolage", "Électricité", "Plomberie", "Jardinage", "Réparation", "Mécanique", "Peinture intérieure", "Ménage", "Repassage"],
  },
  {
    family: "Bien-être & beauté",
    items: ["Massage", "Coiffure", "Maquillage", "Yoga à domicile"],
  },
  {
    family: "Famille & quotidien",
    items: ["Baby-sitting", "Garde d'animaux", "Promenade de chien", "Aide au déménagement", "Transport", "Covoiturage"],
  },
  {
    family: "Créatif & digital",
    items: ["Couture & retouche", "Photographie", "Montage vidéo", "Design", "Musique", "Informatique", "Aide informatique", "Dépannage informatique", "Traduction"],
  },
  {
    family: "Cuisine",
    items: ["Cuisine à domicile"],
  },
  {
    family: "Accompagnement & conseil",
    items: ["Cours d'anglais", "Cours de maths", "Cours de musique", "Cours de sport", "Soutien scolaire", "Coaching sportif", "Accompagnement", "Organisation d'événements", "Conseils professionnels", "Aide administrative"],
  },
  {
    family: "Immobilier",
    items: ["Gestion locative", "État des lieux", "Home staging", "Diagnostic immobilier", "Conciergerie"],
  },
  {
    family: "Mode",
    items: ["Relooking", "Personal shopper", "Stylisme"],
  },
  {
    family: "Santé",
    items: ["Infirmier à domicile", "Aide-soignant à domicile", "Kinésithérapie", "Ostéopathie", "Diététique"],
  },
  { family: "Autre", items: ["Libre"] },
];

const dedupe = (arr: string[]) => Array.from(new Set(arr));

export const TOUTES_ACTIVITES = dedupe(PASSION_GROUPS.flatMap(g => g.items)).sort();
export const TOUS_SERVICES = dedupe(POWER_GROUPS.flatMap(g => g.items)).sort();

export const PASSION_TO_FAMILY: Record<string, string> = Object.fromEntries(
  PASSION_GROUPS.flatMap(g => g.items.map(item => [item, g.family]))
);
export const POWER_TO_FAMILY: Record<string, string> = Object.fromEntries(
  POWER_GROUPS.flatMap(g => g.items.map(item => [item, g.family]))
);

// Un rituel peut être une passion récurrente (padel du mardi) comme un power récurrent (cours
// de cuisine chaque semaine) — on fusionne donc les deux taxonomies par famille plutôt que de
// maintenir une troisième liste à part, qui finit toujours par prendre du retard sur les deux
// autres (c'est ce qui manquait des sports comme Pilates dans le choix de catégorie d'un rituel).
const mergeGroups = (...groupLists: CategoryGroup[][]): CategoryGroup[] => {
  const byFamily = new Map<string, string[]>();
  for (const groups of groupLists) {
    for (const g of groups) {
      const items = byFamily.get(g.family) || [];
      byFamily.set(g.family, dedupe([...items, ...g.items]));
    }
  }
  return Array.from(byFamily.entries()).map(([family, items]) => ({ family, items }));
};

export const RITUAL_GROUPS: CategoryGroup[] = mergeGroups(PASSION_GROUPS, POWER_GROUPS);
export const TOUS_RITUAL = dedupe(RITUAL_GROUPS.flatMap(g => g.items)).sort();

// Pro Events : événements/activités "entre pros" — visibles uniquement des abonnés d'un lieu
// Pro/Association et/ou des autres lieux Pro/Association (jamais du grand public). Catégories
// couvrant l'entreprise, la formation, le réseautage, le sport pro et la tech, en plus des
// événements "entre lieux" (réseau B2B).
export const PRO_EVENT_GROUPS: CategoryGroup[] = [
  {
    family: "Événements d'entreprise",
    items: [
      "Séminaire", "Soirée d'entreprise", "Team building", "Assemblée générale",
      "Lancement de produit", "Pot de départ", "Afterwork",
    ],
  },
  {
    family: "Formation & développement",
    items: [
      "Formation", "Atelier professionnel", "Masterclass", "Coaching collectif",
      "Certification",
    ],
  },
  {
    family: "Réseautage & business",
    items: [
      "Conférence", "Networking", "Salon professionnel", "Petit-déjeuner d'affaires",
      "Table ronde", "Pitch de startups",
    ],
  },
  {
    family: "Sport & compétition",
    items: [
      "Tournoi sportif", "Challenge inter-entreprises", "Course d'entreprise",
      "Olympiades",
    ],
  },
  {
    family: "Tech & innovation",
    items: [
      "Hackathon", "Meetup tech", "Démo day", "Webinaire",
    ],
  },
  {
    family: "Entre lieux Pro",
    items: [
      "Partenariat entre lieux", "Événement inter-établissements", "Rencontre réseau Pro",
    ],
  },
];
export const TOUS_PRO_EVENTS = dedupe(PRO_EVENT_GROUPS.flatMap(g => g.items)).sort();
